import React, { FunctionComponent, useState, useEffect, useCallback } from 'react';
import { Grid } from '@material-ui/core';
import Loader from '../../components/Loader';
import CitationInfiniteList from './CitationInfiniteList';
import Title from '../../components/PageHeader/Title';
import Header from '../../components/PageHeader/Header';
import { CitationList } from '../../store/modules/citation/types';
import { FilterCondition, Form, Order } from '../../store/modules/application/types';
import Filters from 'src/components/Filters';

type CitationProps = {
    citationList: CitationList;
    isLoading: boolean;
    setError: Function;
    hasMore: boolean;
    citationCount: number;
    citationSearchForm: Form;
    isSearchFormLoading: boolean;
    getCitationSearchForm: Function;
    applyCitationFilters: Function;
    filterSelections: Record<string, string | string[]>;
    setFilterSelections: Function;
};

const Citations: FunctionComponent<CitationProps> = (props) => {
    const [skip, setSkip] = useState<number>(0);
    const limit = 20;

    const {
        citationList,
        citationCount,
        isLoading,
        hasMore,
        citationSearchForm,
        isSearchFormLoading,
        getCitationSearchForm,
        applyCitationFilters,
        filterSelections,
        setFilterSelections,
    } = props;

    const [citationSortOrder, setCitationSortOrder] = useState<Order>('desc');
    const [citationSortColumn, setCitationSortColumn] = useState<string>('citationDate');
    const [listFilters, setListFilters] = useState<FilterCondition[]>([]);
    const [scrollToTopTrigger, setScrollToTopTrigger] = useState<Object | undefined>(undefined);

    useEffect(() => {
        getCitationSearchForm();
    }, [getCitationSearchForm]);
    const refreshCitationsList = useCallback(
        (listFilters, apiProps) => {
            if (
                Object.entries(filterSelections).length === 0 ||
                (Object.entries(filterSelections).length > 0 && listFilters.length > 0)
            ) {
                applyCitationFilters(listFilters, {
                    limit,
                    orderBy: citationSortOrder,
                    orderCol: citationSortColumn,
                    ...apiProps,
                });
            }
        },
        [applyCitationFilters, limit, citationSortOrder, citationSortColumn, filterSelections],
    );

    useEffect(() => {
        refreshCitationsList(listFilters, { skip });
        // The disable-next-line below is to avoid redundant callbacks due to updates in listFilters
        //eslint-disable-next-line
    }, [refreshCitationsList, skip]);

    useEffect(() => {
        if (skip !== 0) {
            setSkip(0);
        } else {
            refreshCitationsList(listFilters, { skip });
        }
        // The disable-next-line below is to avoid redundant callbacks due to updates in skip
        //eslint-disable-next-line
    }, [refreshCitationsList, listFilters]);

    const updateCitationSortBy = (sortBy: string) => {
        setCitationSortColumn(sortBy);
        setSkip(0);
    };
    const updateCitationSortOrder = (sortOrder: Order) => {
        setCitationSortOrder(sortOrder);
        setSkip(0);
    };
    return (
        <div className="verticalFillContainer">
            <Header>
                <Grid container md={2} lg={2} direction="row" alignItems="center">
                    <Title titleText="Citations" />
                </Grid>
                <Grid item md={10} lg={10}>
                    <Filters
                        searchForm={citationSearchForm}
                        isLoading={isSearchFormLoading}
                        setListFilters={setListFilters}
                        selections={filterSelections}
                        handleFilterSelections={setFilterSelections}
                        setListScrollToTopTrigger={setScrollToTopTrigger}
                    />
                </Grid>
            </Header>
            <div className="verticalFillContent">
                {!citationList ? (
                    <Loader />
                ) : (
                    <CitationInfiniteList
                        citationCount={citationCount}
                        citationList={citationList}
                        isLoading={isLoading}
                        skip={skip}
                        limit={limit}
                        setSkip={setSkip}
                        hasMore={hasMore}
                        sortBy={citationSortColumn}
                        sortOrder={citationSortOrder}
                        setSortOrder={updateCitationSortOrder}
                        setSortColumn={updateCitationSortBy}
                        scrollToTopTrigger={scrollToTopTrigger}
                        listFilters={listFilters}
                    />
                )}
            </div>
        </div>
    );
};

export default Citations;
